import { useRef, useState } from 'react'
import QRCode from 'qrcode'
import { toast } from 'sonner'
import { Download } from 'lucide-react'
import { useBrands, useProducts, useSubscriptions } from '../hooks/useApi'
import { getPublicCatalogHref, waitForImages } from '../lib/catalogUrl'
import { normalizePlanId } from '../lib/plans'
import { downloadCatalogPdf } from '../lib/downloadCatalogPdf'
import { CatalogBrochureTemplate, type BrochureProduct } from './CatalogBrochureTemplate'

interface CatalogPdfDownloadProps {
  className?: string
  mobileBlock?: boolean
  /** Teks putih saat navbar gradient (belum di-scroll) */
  lightText?: boolean
}

export function CatalogPdfDownload({ className = '', mobileBlock = false, lightText = false }: CatalogPdfDownloadProps) {
  const { data: brands } = useBrands()
  const { data: products } = useProducts()
  const { data: subscriptions } = useSubscriptions()
  const brochureRef = useRef<HTMLDivElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [showBrochure, setShowBrochure] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState('')

  const primaryBrand = brands?.[0]
  const plan = normalizePlanId(subscriptions?.[0]?.plan)
  const catalogUrl = primaryBrand
    ? getPublicCatalogHref({
        slug: primaryBrand.slug,
        subdomain: primaryBrand.subdomain,
        plan,
      })
    : ''
  const brandProducts = (products || []).filter(
    (p: BrochureProduct & { brandId?: number }) =>
      !primaryBrand?.id || p.brandId === primaryBrand.id
  )

  const handleDownload = async () => {
    if (!primaryBrand) {
      toast.error('Buat brand terlebih dahulu untuk mengunduh brosur')
      return
    }

    setIsDownloading(true)

    try {
      const qr = await QRCode.toDataURL(catalogUrl, {
        width: 280,
        margin: 2,
        color: { dark: '#0f2b60', light: '#ffffff' },
      })
      setQrDataUrl(qr)
      setShowBrochure(true)

      // Wait until React mounts the hidden brochure template
      await new Promise<void>((resolve) => {
        const waitForRef = () => {
          if (brochureRef.current) resolve()
          else requestAnimationFrame(waitForRef)
        }
        requestAnimationFrame(waitForRef)
      })

      const el = brochureRef.current!
      await waitForImages(el)

      await downloadCatalogPdf(
        el,
        `brosur-${primaryBrand.slug}-${new Date().toISOString().split('T')[0]}.pdf`
      )
      toast.success('Brosur PDF berhasil diunduh')
    } catch {
      toast.error('Gagal mengunduh brosur PDF')
    } finally {
      setShowBrochure(false)
      setIsDownloading(false)
    }
  }

  const label = isDownloading ? 'Mengunduh...' : 'Unduh Brosur PDF'

  const btnClass = mobileBlock
    ? `w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${className}`
    : `inline-flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap disabled:opacity-50 transition-colors ${
        lightText
          ? 'text-white hover:text-white hover:bg-white/10'
          : 'text-gray-600 hover:text-gray-900'
      } ${className}`

  return (
    <>
      {showBrochure && primaryBrand && qrDataUrl && (
        <CatalogBrochureTemplate
          ref={brochureRef}
          brand={primaryBrand}
          products={brandProducts}
          qrDataUrl={qrDataUrl}
          catalogUrl={catalogUrl}
        />
      )}
      <button type="button" onClick={handleDownload} disabled={isDownloading} className={btnClass}>
        <Download className="h-4 w-4 shrink-0" />
        {label}
      </button>
    </>
  )
}
